import type { DashboardRule } from '@/optimizer';
import moment from 'moment';
import type { MediaLimit, OptimizedPlan } from '@/dashboards/shared/optimisation/optimisationSession';

export type ChannelBound = { min: number; max: number; recommended: number };
export type ChannelBounds = Record<string, ChannelBound>;

// Same formula as getDurationRatio in src/optimizer/createOptimizerServiceManager.ts (366-day year).
export const campaignDurationRatio = (startDate: string, endDate: string): number => {
    const days = moment(endDate, 'DD-MM-YYYY').diff(moment(startDate, 'DD-MM-YYYY'), 'days') + 1;
    return days / 366;
};

const appliesToCampaign = (rule: DashboardRule) => rule.target === 'all' || rule.target === 'campaign';

export const channelBounds = ({
    baseLimits,
    startDate,
    endDate,
    rules,
    result,
}: {
    baseLimits: MediaLimit[];
    startDate: string;
    endDate: string;
    rules: DashboardRule[];
    result: OptimizedPlan | null;
}): ChannelBounds => {
    const ratio = campaignDurationRatio(startDate, endDate);
    const bounds: ChannelBounds = {};

    for (const limit of baseLimits) {
        let min = Math.round(limit.lowerLimit * ratio);
        let max = Math.round(limit.upperLimit * ratio);

        for (const rule of rules) {
            if (rule.channel !== limit.medium || rule.type !== 'Total budget' || !appliesToCampaign(rule)) continue;
            const value = Math.round(Number(rule.value) * ratio);
            if (rule.subtype === 'minimum') min = Math.max(min, value);
            if (rule.subtype === 'maximum') max = Math.min(max, value);
        }

        bounds[limit.medium] = {
            min,
            max,
            recommended: result?.optimalVariables[limit.medium]?.optimalSpend ?? 0,
        };
    }

    return bounds;
};
